import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Collapse,
  IconButton,
  Box,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { smartOnboardingApi } from '../api/smartOnboarding';

interface OnboardingReminderProps {
  /** Force show even if dismissed (for settings page) */
  forceShow?: boolean;
}

const DISMISS_KEY = 'onboarding_reminder_dismissed';
const DISMISS_EXPIRY_KEY = 'onboarding_reminder_dismiss_expiry';
const DISMISS_DURATION_HOURS = 24; // Reminder comes back after 24 hours

const OnboardingReminder: React.FC<OnboardingReminderProps> = ({ forceShow = false }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [missingSteps, setMissingSteps] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkOnboardingStatus();
  }, [forceShow]);

  const checkOnboardingStatus = async () => {
    try {
      // Check if onboarding is already complete
      const isComplete = localStorage.getItem('onboarding_completed') === 'true';
      if (isComplete) {
        setVisible(false);
        setLoading(false);
        return;
      }

      // Check if reminder was dismissed and if dismissal has expired
      if (!forceShow) {
        const dismissed = localStorage.getItem(DISMISS_KEY) === 'true';
        const expiryStr = localStorage.getItem(DISMISS_EXPIRY_KEY);
        
        if (dismissed && expiryStr) {
          const expiry = new Date(expiryStr);
          if (new Date() < expiry) {
            // Still within dismissal period
            setVisible(false);
            setLoading(false);
            return;
          } else {
            // Dismissal expired, clear it
            localStorage.removeItem(DISMISS_KEY);
            localStorage.removeItem(DISMISS_EXPIRY_KEY);
          }
        }
      }

      // Try to get progress from backend
      try {
        const progressData = await smartOnboardingApi.getProgress();
        setProgress(progressData.completion_percentage || 0);
        setMissingSteps(progressData.missing_critical || []);
      } catch (error) {
        // Backend might not be available, check localStorage
        const savedData = localStorage.getItem('onboarding_data');
        if (savedData) {
          try {
            const data = JSON.parse(savedData);
            // Calculate approximate progress based on filled fields
            const filledSteps = Object.keys(data).filter(key => 
              data[key] && Object.keys(data[key]).length > 0
            );
            setProgress((filledSteps.length / 7) * 100); // 7 total steps
          } catch {
            setProgress(0);
          }
        } else {
          setProgress(0);
        }
      }

      setVisible(true);
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    // Set dismissal with expiry
    localStorage.setItem(DISMISS_KEY, 'true');
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + DISMISS_DURATION_HOURS);
    localStorage.setItem(DISMISS_EXPIRY_KEY, expiry.toISOString());
    setVisible(false);
  };

  const handleContinueOnboarding = () => {
    navigate('/onboarding');
  };

  if (loading || !visible) {
    return null;
  }

  return (
    <Collapse in={visible}>
      <Alert
        severity="info"
        icon={<AssignmentIcon />}
        sx={{
          mb: 2,
          borderRadius: 2,
          '& .MuiAlert-message': { width: '100%' },
        }}
        action={
          <IconButton
            aria-label="dismiss"
            color="inherit"
            size="small"
            onClick={handleDismiss}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
      >
        <AlertTitle sx={{ fontWeight: 600 }}>
          Complete Your Setup
        </AlertTitle>
        
        <Box sx={{ width: '100%', mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {progress > 0 
                ? `You're ${Math.round(progress)}% done with setup`
                : 'Set up your supply chain profile for personalized insights'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 6, 
              borderRadius: 1,
              bgcolor: 'rgba(0,0,0,0.1)',
            }} 
          />
        </Box>

        {missingSteps.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Missing: {missingSteps.slice(0, 3).join(', ')}
            {missingSteps.length > 3 && ` and ${missingSteps.length - 3} more`}
          </Typography>
        )}

        <Button
          size="small"
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={handleContinueOnboarding}
          sx={{ 
            mt: 1,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          {progress > 0 ? 'Continue Setup' : 'Start Setup'}
        </Button>
      </Alert>
    </Collapse>
  );
};

export default OnboardingReminder;
